"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"

import Modal from "@modules/common/components/modal"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import X from "@modules/common/icons/x"
import PromoBadge from "@modules/common/components/promo-badge"
import { MagnifyingGlass } from "@medusajs/icons"
import { BiTransfer } from "react-icons/bi"

import { API_BASE_URL } from "@lib/config"
import { formatCurrency, formatDiscountedCurrency } from "@lib/util/formatCurrency"
import { readJsonResponse } from "@lib/http/read-json-response"
import { resolveProductHref } from "@lib/util/product-href"
import { resolveTransferUnitPricing } from "@lib/util/transfer-pricing"
import { usePaymentMethods } from "@context/payment-methods-context"
import { useAnalytics } from "@context/analytics-context"
import { createBrowserEventId } from "@lib/analytics/event-id"
import { sendMetaAnalyticsCompanion } from "@lib/analytics/meta-companion-client"
import {
  beginMetaSearchRequest,
  claimCompletedMetaSearch,
  createMetaSearchLifecycleState,
} from "@lib/analytics/meta-search-lifecycle"

type SearchBarProps = {
  isOpen: boolean
  onClose: () => void
}

type ProductHit = {
  _id: string
  name: string
  slug: string
  price: number
  originalPrice?: number
  currency?: string
  discountedPrice?: number | null
  discount?: number | null
  isSale?: boolean
  disableTransferDiscount?: boolean
  images?: Array<{ url?: string } | string>
  thumbnail?: string
  brand?: { slug?: string | null }
  metaCatalogContentIds?: string[]
}

const SEARCH_DEBOUNCE_MS = 250
const RESULTS_LIMIT = 12
const RECOMMENDED_FETCH_LIMIT = 24
const RECOMMENDED_DISPLAY_LIMIT = 8

const shuffleArray = <T,>(source: T[]): T[] => {
  const array = [...source]
  for (let index = array.length - 1; index > 0; index--) {
    const swapWith = Math.floor(Math.random() * (index + 1))
    ;[array[index], array[swapWith]] = [array[swapWith], array[index]]
  }
  return array
}

const resolveProductImage = (product: ProductHit) => {
  if (product.thumbnail) {
    return product.thumbnail
  }
  const firstImage = Array.isArray(product.images) ? product.images[0] : undefined
  if (!firstImage) {
    return null
  }
  if (typeof firstImage === "string") {
    return firstImage
  }
  return firstImage?.url ?? null
}

export default function SearchBar({ isOpen, onClose }: SearchBarProps) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<ProductHit[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [recommendedProducts, setRecommendedProducts] = useState<ProductHit[]>([])
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false)
  const { wire } = usePaymentMethods()
  const analytics = useAnalytics()

  const inputRef = useRef<HTMLInputElement | null>(null)
  const abortController = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const metaSearchLifecycleRef = useRef(createMetaSearchLifecycleState())

  const closeAndReset = useCallback(() => {
    setQuery("")
    setResults([])
    setError(null)
    setIsLoading(false)
    setHasSearched(false)
    setRecommendedProducts([])
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const focusTimeout = setTimeout(() => {
      inputRef.current?.focus()
    }, 50)

    return () => {
      clearTimeout(focusTimeout)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        closeAndReset()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [closeAndReset, isOpen])

  useEffect(() => {
    const metaRequestId = beginMetaSearchRequest(metaSearchLifecycleRef.current)
    if (!isOpen) {
      return
    }

    const trimmed = query.trim()
    if (!trimmed) {
      if (abortController.current) {
        abortController.current.abort()
        abortController.current = null
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      setResults([])
      setIsLoading(false)
      setError(null)
      return
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(async () => {
      if (abortController.current) {
        abortController.current.abort()
      }

      const controller = new AbortController()
      abortController.current = controller

      setIsLoading(true)
      setError(null)

      try {
        const url = new URL(`${API_BASE_URL}/public/products`)
        url.searchParams.set("q", trimmed)
        url.searchParams.set("limit", String(RESULTS_LIMIT))

        const response = await fetch(url.toString(), {
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
        })

        if (!response.ok) {
          throw new Error(`Search failed with status ${response.status}`)
        }

        const payload: any = await readJsonResponse(response, {
          context: "product search",
        })
        const items = Array.isArray(payload?.products) ? (payload.products as ProductHit[]) : []
        setResults(items)
        setHasSearched(true)
        const normalizedQuery = claimCompletedMetaSearch({
          state: metaSearchLifecycleRef.current,
          requestId: metaRequestId,
          query: trimmed,
          succeeded: true,
        })
        if (normalizedQuery) {
          const eventId = createBrowserEventId()
          const catalogContentIds = Array.from(
            new Set(
              items.flatMap((item) =>
                Array.isArray(item.metaCatalogContentIds)
                  ? item.metaCatalogContentIds
                  : []
              )
            )
          ).slice(0, 100)
          analytics.trackSearch(
            normalizedQuery,
            { eventId },
            catalogContentIds
          )
          const signals = analytics.getMetaSignals()
          if (analytics.metaCapiActive && signals) {
            void sendMetaAnalyticsCompanion({
              kind: "search",
              eventId,
              signals,
              searchQuery: normalizedQuery,
              catalogContentIds,
            }).catch((companionError) => {
              if (process.env.NODE_ENV !== "production") {
                console.warn("Meta Search companion failed", companionError)
              }
            })
          }
        }
      } catch (err) {
        if ((err as any)?.name === "AbortError") {
          return
        }
        console.error("Search request failed", err)
        setError("We couldn't complete your search. Please try again.")
        setResults([])
        setHasSearched(true)
      } finally {
        setIsLoading(false)
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [analytics, isOpen, query])

  useEffect(() => {
    if (!isOpen) {
      if (abortController.current) {
        abortController.current.abort()
        abortController.current = null
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [isOpen])

  const suggestions = useMemo(() => {
    return results.slice(0, 4)
  }, [results])

  const fetchRecommendedProducts = useCallback(async () => {
    if (isLoadingRecommendations) {
      return
    }

    setIsLoadingRecommendations(true)
    try {
      const url = new URL(`${API_BASE_URL}/public/products`)
      url.searchParams.set("limit", String(RECOMMENDED_FETCH_LIMIT))

      const response = await fetch(url.toString(), {
        headers: { "Content-Type": "application/json" },
      })

      if (!response.ok) {
        throw new Error(`Recommendations failed with status ${response.status}`)
      }

      const payload: any = await readJsonResponse(response, {
        context: "recommended products",
      })
      const items = Array.isArray(payload?.products) ? (payload.products as ProductHit[]) : []
      const randomized = shuffleArray(items).slice(0, RECOMMENDED_DISPLAY_LIMIT)
      setRecommendedProducts(randomized)
    } catch (err) {
      console.error("Recommended products request failed", err)
      setRecommendedProducts([])
    } finally {
      setIsLoadingRecommendations(false)
    }
  }, [isLoadingRecommendations])

  useEffect(() => {
    if (!isOpen || !hasSearched || isLoading) {
      return
    }

    if (results.length > 0 || recommendedProducts.length > 0) {
      return
    }

    fetchRecommendedProducts()
  }, [fetchRecommendedProducts, hasSearched, isLoading, isOpen, recommendedProducts.length, results.length])

  const renderProductGrid = (products: ProductHit[]) => (
    <div className="grid grid-cols-2 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {products.map((product) => {
        const href = resolveProductHref(product)
        const originalPrice =
          typeof product.originalPrice === "number"
            ? product.originalPrice
            : product.price
        const hasDiscount =
          typeof product.discountedPrice === "number" &&
          product.discountedPrice > 0 &&
          product.discountedPrice < originalPrice
        const price = hasDiscount ? product.discountedPrice! : originalPrice
        const isSale = product.isSale === true
        const computedPercent =
          hasDiscount && !isSale && originalPrice > 0
            ? Math.round((1 - price / originalPrice) * 100)
            : undefined
        const displayPercent =
          !isSale &&
          typeof product.discount === "number" &&
          product.discount > 0
            ? Math.round(product.discount)
            : computedPercent
        const { showTransferPrice, transferPrice } =
          resolveTransferUnitPricing({
            unitPrice: price,
            disableTransferDiscount: product.disableTransferDiscount,
            settings: wire,
          })
        const imageUrl = resolveProductImage(product)

        return (
          <LocalizedClientLink
            key={product._id}
            href={href}
            className="group flex flex-col"
            onClick={closeAndReset}
          >
            <div className="relative overflow-hidden rounded-lg bg-gray-100 transition group-hover:shadow-md aspect-[3/4]">
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt={product.name}
                  fill
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 20vw"
                  className="object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
                  No image
                </div>
              )}
              {(isSale || (hasDiscount && displayPercent)) && (
                <PromoBadge className="absolute right-3 top-3 flex h-[54px] w-[54px] items-center justify-center text-center text-[10px] font-lealti-light uppercase tracking-wide">
                  {isSale ? "SALE" : `${displayPercent}% OFF`}
                </PromoBadge>
              )}
            </div>
            <div className="mt-4 text-sm font-lealti-light text-gray-900">{product.name}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm font-lealti-light text-gray-600">
              {hasDiscount && (
                <span className="text-xs text-gray-400 line-through">
                  {formatCurrency(originalPrice, product.currency || "UYU")}
                </span>
              )}
              {hasDiscount && isSale ? (
                <PromoBadge className="flex h-6 min-w-12 items-center justify-center px-2 text-[10px] font-lealti-light uppercase tracking-wide">
                  SALE
                </PromoBadge>
              ) : hasDiscount && displayPercent ? (
                <PromoBadge className="flex h-6 min-w-14 items-center justify-center px-2 text-[10px] font-lealti-light uppercase tracking-wide">
                  {displayPercent}% OFF
                </PromoBadge>
              ) : null}
              <span>
                {hasDiscount
                  ? formatDiscountedCurrency(price, product.currency || "UYU")
                  : formatCurrency(price, product.currency || "UYU")}
              </span>
            </div>
            {showTransferPrice && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-black">
                <span className="flex items-center gap-1 rounded-xs border border-black bg-white px-2 py-1 text-[9px] font-lealti-light tracking-[0.05em]">
                  <BiTransfer className="h-3 w-3" />
                  TRANSFERENCIA
                </span>
                <span className="text-sm font-lealti-light">
                  {formatDiscountedCurrency(
                    transferPrice,
                    product.currency || "UYU"
                  )}
                </span>
              </div>
            )}
          </LocalizedClientLink>
        )
      })}
    </div>
  )

  const renderContent = () => {
    if (!query.trim() && !isLoading) {
      return (
        <div className="flex flex-1 items-center justify-center text-sm text-gray-500 font-lealti-light">
          Escribe para buscar productos
        </div>
      )
    }

    if (isLoading) {
      return (
        <div className="flex flex-1 items-center justify-center text-sm text-gray-500 font-lealti-light">
          Buscando productos...
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex flex-1 items-center justify-center text-sm text-gray-500 text-center px-4 font-lealti-light">
          {error}
        </div>
      )
    }

    if (hasSearched && results.length === 0) {
      return (
        <div className="mt-6 flex-1 overflow-y-auto pb-6">
          <div className="flex items-center justify-center px-4 text-center text-sm text-gray-500 font-lealti-light">
            No se encontraron resultados para “{query.trim()}”.
          </div>
          {(isLoadingRecommendations || recommendedProducts.length > 0) && (
            <div className="mt-8">
              <p className="text-sm font-lealti-light text-gray-900 sm:text-base">Productos recomendados</p>
              {isLoadingRecommendations ? (
                <div className="mt-4 flex items-center justify-center text-sm font-lealti-light text-gray-500">
                  Cargando productos recomendados...
                </div>
              ) : (
                <div className="mt-4">{renderProductGrid(recommendedProducts)}</div>
              )}
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="mt-6 flex-1 overflow-y-auto pb-6">
        {renderProductGrid(results)}
      </div>
    )
  }

  return (
    <Modal isOpen={isOpen} close={closeAndReset} size="full" search data-testid="search-modal">
      <div className="flex h-full w-full flex-col rounded-none bg-white px-4 pb-6 pt-6 shadow-none sm:mx-auto sm:h-[70vh] sm:max-w-5xl sm:rounded-3xl sm:px-10 lg:px-12">
        <div className="flex items-center gap-3 border-b border-gray-200 pb-4">
          <MagnifyingGlass className="h-6 w-6 flex-shrink-0 text-gray-500" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar"
            aria-label="Buscar productos"
            className="h-10 flex-1 border-0 bg-transparent text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 sm:text-lg font-lealti-light"
          />
          <button
            type="button"
            onClick={closeAndReset}
            aria-label="Close search"
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        {suggestions.length > 0 && query.trim() && (
          <div className="mt-4">
            <p className="text-sm font-lealti-light text-gray-500">Sugerencias</p>
            <ul className="mt-2 flex flex-col gap-1">
              {suggestions.map((product) => (
                <li key={`suggestion-${product._id}`}>
                  <button
                    type="button"
                    onClick={() => setQuery(product.name)}
                    className="w-full text-left text-base text-gray-900 transition hover:text-gray-500 font-lealti-light"
                  >
                    {product.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {renderContent()}
      </div>
    </Modal>
  )
}
